/**
 * Public leads RPC definitions
 *
 * The marketing site's "Request your agent" / "Get my quote" forms write a
 * `leads` row through this single unauthenticated endpoint — no authentication
 * required, but rate-limited like the other public writes.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Schema } from "effect";
import { PublicRateLimitMiddleware } from "../rate-limit.tags.ts";

// ============================================================================
// Request Schemas
// ============================================================================

/** Which public page hosted the form — becomes `leads.source_detail` (`public-site:<page>-form`). */
export const LeadRequestPage = Schema.Literal("home", "pricing");
export type LeadRequestPage = typeof LeadRequestPage.Type;

/** The board's "Where should it run?" radio group, verbatim. */
export const LeadRequestWhere = Schema.Literal("Managed cloud", "My own Mac", "Not sure yet");
export type LeadRequestWhere = typeof LeadRequestWhere.Type;

/** Bounded so an unauthenticated caller cannot store megabytes per request (the columns are unbounded `varchar`/`text`). */
export const LeadRequestInput = Schema.Struct({
  name: Schema.NonEmptyTrimmedString.pipe(Schema.maxLength(200)),
  email: Schema.NonEmptyTrimmedString.pipe(Schema.maxLength(320)),
  phone: Schema.optional(Schema.String.pipe(Schema.maxLength(40))),
  need: Schema.NonEmptyTrimmedString.pipe(Schema.maxLength(2000)),
  where: LeadRequestWhere,
  page: LeadRequestPage,
});
export type LeadRequestInput = typeof LeadRequestInput.Type;

/** Only the id crosses the public boundary — never the lead row (notes, status, assignment are internal). */
export const LeadRequestResult = Schema.Struct({ id: Schema.String });
export type LeadRequestResult = typeof LeadRequestResult.Type;

// ============================================================================
// Error Types
// ============================================================================

/**
 * Database error
 * @since 1.0.0
 */
export class DatabaseError extends Schema.TaggedError<DatabaseError>()("DatabaseError", {
  message: Schema.optional(Schema.String),
}) {}

/**
 * Validation error for invalid input
 * @since 1.0.0
 */
export class ValidationError extends Schema.TaggedError<ValidationError>()("ValidationError", {
  field: Schema.String,
  message: Schema.String,
}) {}

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Public leads RPC group — the marketing site's request / quote form backend.
 *
 * @since 1.0.0
 */
export class LeadsPublicRpcs extends RpcGroup.make(
  Rpc.make("leads.request", {
    payload: LeadRequestInput,
    success: LeadRequestResult,
    error: Schema.Union(ValidationError, DatabaseError),
  }).middleware(PublicRateLimitMiddleware),
) {}
